// src/com/veh_type_group/VehTypeGroup.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import {ContentConainer, ContentBody} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppWord } from '../../value/AppConst';
import {
    LIST_VEH_TYPE_GROUP, ADD_VEH_TYPE_GROUP, EDIT_VEH_TYPE_GROUP, REMOVE_VEH_TYPE_GROUP,
    makeVehTypeGroupTree
} from './VehTypeGroupGql';
import { VehTypeInfoDictionary } from '../veh_type_info/VehTypeInfoDict';
import { vehTypeGroupTreeRepo, vehTypeGroupOrderListRepo } from '../../value/AppModel';
import { useAlertSetter } from '../../api/app-hook';
import VehTypeGroupTree from './VehTypeGroupTree';
import { VzmDataText, VzmLabel } from '../../common/VzmStyled';
import VehTypeGroupOrder from '../veh_type_group_order/VehTypeGroupOrder';
import VehTypeGroupCodeSelect from '../veh_type_group_code/VehTypeGroupCodeSelect';

const TreeBox = styled(Box)({marginRight:10, height:'100%', position:'relative', width:420});
const TreeInnerWrapper = styled(Box)({
    padding:0, overflow:'auto', position:'absolute', top:0, left:0, right:0, bottom:0
});
const VehTypeOfGroupBox = styled(Box)({flexGrow:1, display:'flex', flexDirection:'column'});
const DcAndTypeSelectedBox = styled(Box)({
    padding:10, display: 'flex', alignItems: 'center', backgroundColor: '#f0f0f0'
});

const ErrorTitle = VehTypeInfoDictionary.errorTitle;

/**
 * 차종그룹 관리.
 * @param {*} param0 
 * @returns 
 */
export default function VehTypeGroup({
    tabsForSwitchingView // for switching view
}) {
    const vehTypeGroupTreeData = useReactiveVar(vehTypeGroupTreeRepo);
    const [selectedDc, setSelectedDc] = useState({}); // {dcCd, vehTypeGpCd, dcName, groupName}
    const [typeSelectorState, setTypeSelectorState] = useState({open:false, vehTypeGroup: null});
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getVehTypeGroupList, responseList] = useLazyQuery(LIST_VEH_TYPE_GROUP, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => {addAlert({title: ErrorTitle.Get, message: [error.message]})},
    });

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addVehTypeGroup, responseAdd] = useMutation( ADD_VEH_TYPE_GROUP, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => {addAlert({title: ErrorTitle.Add, message: [error.message]})},
	} );
    const [editVehTypeGroup, responseEdit] = useMutation( EDIT_VEH_TYPE_GROUP, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => {addAlert({title: ErrorTitle.Edit, message: [error.message]})},
	} );
    const [removeVehTypeGroup, responseRemove] = useMutation( REMOVE_VEH_TYPE_GROUP, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => {addAlert({title: ErrorTitle.Remove, message: [error.message]})},
	});

    const getVehTypeGroupListNow = () => {
        getVehTypeGroupList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(()=>{
        let diff = new Date().getTime()/1000 - vehTypeGroupTreeData.when;
        if(diff > 60) getVehTypeGroupListNow();
    },[]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehTypeGroupList) {
            vehTypeGroupTreeRepo({
                when: new Date().getTime()/1000,
                records: makeVehTypeGroupTree(data.vehTypeGroupList)
            });
        }
    };

    const onCompleteAdd = (data, clientOption) => {
        setTypeSelectorState({open:false, vehTypeGroup: null});
        if(data.vehTypeGroupAdd.ok) {
            getVehTypeGroupListNow();
        }
        else
            addAlert({title: ErrorTitle.Add, message: [data.vehTypeGroupAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        setTypeSelectorState({open:false, vehTypeGroup: null});
        if(data.vehTypeGroupEdit.ok) {
            getVehTypeGroupListNow();
        }
        else
            addAlert({title: ErrorTitle.Add, message: [data.vehTypeGroupEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.vehTypeGroupRemove.ok) {
            getVehTypeGroupListNow();
            setSelectedDc({});
            vehTypeGroupOrderListRepo({when:0, records:[]});
        }
        else
            addAlert({title: ErrorTitle.Add, message: [data.vehTypeGroupEdit.message]});
    };

    // ************** Event Handlers **************

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getVehTypeGroupListNow();
    };

    // DC에 대한 차종그룹 설정을 추가.
    const onRequestAdd = (item) => {
        setTypeSelectorState({open:true, vehTypeGroup: item});
    };

    // DC에 대한 차종그룹 설정을 변경.
    const onRequestEdit = (item) => {
        setTypeSelectorState({open:true, vehTypeGroup: item});
    };

    const confirmRemoveVehTypeGroup = async (item) => {
        const confirm = await openConfirmDialog({
            title: '차종그룹 설정 삭제',
            content: [
                `차종그룹 설정 ${item.groupName}을 삭제합니다.`,
                'DC별 차종그룹 설정은 많은 프로세스에 영향을 줄 수 있습니다.',
                '삭제후에는 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });
        if(confirm) removeVehTypeGroup({variables: {vehTypeGroup: {
            vehTypeGpCd: item.vehTypeGpCd, dcCd: item.dcCd
        }}});
    };

    // DC에 대한 차종그룹 설정을 삭제.
    const onRequestRemove = (item) => {
        confirmRemoveVehTypeGroup(item);
    }

    // 차종그룹 선택창을 닫음.
    const onCloseSelector = () => {
        setTypeSelectorState({open:false, vehTypeGroup: null});
    };

    // 차종그룹 선택창에서 차종그룹을 선택함.
    const onSelectVehTypeGroupForDc = (vehTypeGroupCode) => {
        const param = {
            variables: {
                vehTypeGroup: {
                    vehTypeGpCd: vehTypeGroupCode.vehTypeGpCd,
                    dcCd: typeSelectorState.vehTypeGroup.dcCd
                }
            }
        };

        if(typeSelectorState.vehTypeGroup.vehTypeGpCd) {
            editVehTypeGroup(param);
        }
        else {
            addVehTypeGroup(param);
        }
    };

    // click on tree node in VehTypeGroupTree
    const onSelectDcAndGroup = (item) => {
        vehTypeGroupOrderListRepo({when:0, records:[]});
        setSelectedDc(item);
    };

    const loadingAny
    = responseList.loading
    || responseAdd.loading
    || responseEdit.loading
    || responseRemove.loading;

    return(
        <ContentConainer>
            {tabsForSwitchingView}
            <ContentBody style={{display:'flex'}}>
                <TreeBox>
                    <TreeInnerWrapper>
                        <VehTypeGroupTree
                            loadingAny={loadingAny}
                            tree={vehTypeGroupTreeData.records}
                            selected={selectedDc}
                            onSelection={onSelectDcAndGroup}
                            onRequestAdd={onRequestAdd}
                            onRequestEdit={onRequestEdit}
                            onRequestRemove={onRequestRemove}
                            onRequestReload={onClickReload}
                        />
                    </TreeInnerWrapper>
                </TreeBox>
                <VehTypeOfGroupBox>
                    <DcAndTypeSelectedBox>
                        {
                            selectedDc.vehTypeGpCd ?
                            <>
                            <VzmLabel>물류센터</VzmLabel>
                            <VzmDataText>{selectedDc.dcName}</VzmDataText>
                            <VzmLabel style={{marginLeft:30}}>차종그룹</VzmLabel>
                            <VzmDataText>{selectedDc.groupName}</VzmDataText>
                            </>
                            : <span>물류센터와 차종그룹을 선택하세요.</span>
                        }
                        
                    </DcAndTypeSelectedBox>
                    <VehTypeGroupOrder
                        vehTypeGroup={selectedDc}
                    />
                </VehTypeOfGroupBox>
            </ContentBody>
            <VehTypeGroupCodeSelect
                open={typeSelectorState.open}
                vehTypeGroup={typeSelectorState.vehTypeGroup}
                onClose={onCloseSelector}
                onSelect={onSelectVehTypeGroupForDc}
            />
        </ContentConainer>
    );
}